import { fastApiURL } from '../utils/config';
import TokenManager from '../utils/tokenManager';

const KEYCLOAK_URL = process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8080';
const KEYCLOAK_REALM = process.env.REACT_APP_KEYCLOAK_REALM || 'ruractive-dss';
const KEYCLOAK_CLIENT_ID = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'frontend-client';

class AuthService {
  constructor() {
    this.keycloakTokenUrl = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`;
    this.keycloakLogoutUrl = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/logout`;
    this.apiBaseUrl = `${fastApiURL}/api/auth`;
  }

  async login(email, password) {
    try {
      const formData = new URLSearchParams();
      formData.append('grant_type', 'password');
      formData.append('client_id', KEYCLOAK_CLIENT_ID);
      formData.append('username', email);
      formData.append('password', password);

      let response;
      try {
        response = await fetch(this.keycloakTokenUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData,
        });
      } catch (fetchError) {
        // Network error - likely CORS or server not reachable
        if (fetchError.name === 'TypeError' && fetchError.message.includes('fetch')) {
          throw new Error(
            `Cannot connect to Keycloak at ${this.keycloakTokenUrl}. ` +
            `Please check if Keycloak is running and accessible. ` +
            `If using Docker, ensure Keycloak is running and CORS is configured.`
          );
        }
        throw fetchError;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error_description || 
                           errorData.error || 
                           `Login failed with status ${response.status}`;
        throw new Error(errorMessage);
      }

      const tokenData = await response.json();
      
      // Store tokens
      TokenManager.setTokens(
        tokenData.access_token,
        tokenData.refresh_token,
        tokenData.expires_in
      );

      // Get user profile from backend
      const userProfile = await this.getUserProfile();
      
      return {
        tokens: tokenData,
        user: userProfile,
      };
    } catch (error) {
      // Preserve the original error message if it's already descriptive
      if (error.message && error.message.includes('Keycloak')) {
        throw error;
      }
      throw new Error(error.message || 'Login failed');
    }
  }

  async register(userData) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `Registration failed (${response.status})`;
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      // Handle both individual (UserProfileResponse) and organization (pending) responses
      return result;
    } catch (error) {
      // If it's already an Error object, rethrow it; otherwise create a new one
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(error?.message || error?.detail || 'Registration failed');
    }
  }

  async createOrganizationUser(userData) {
    try {
      const token = TokenManager.getAccessToken();
      if (!token) {
        throw new Error('No token available');
      }

      const response = await fetch(`${this.apiBaseUrl}/organizations/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `Failed to create organization user (${response.status})`;
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(error?.message || error?.detail || 'Failed to create organization user');
    }
  }

  async getOrganizationUsers() {
    try {
      const token = TokenManager.getAccessToken();
      if (!token) {
        throw new Error('No token available');
      }

      const response = await fetch(`${this.apiBaseUrl}/organizations/users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `Failed to get organization users (${response.status})`;
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(error?.message || error?.detail || 'Failed to get organization users');
    }
  }

  async inviteUser(email, roleName = 'user') {
    try {
      const token = TokenManager.getAccessToken();
      if (!token) {
        throw new Error('No token available');
      }

      const response = await fetch(`${this.apiBaseUrl}/organizations/users/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ email, role_name: roleName }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `Failed to invite user (${response.status})`;
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(error?.message || error?.detail || 'Failed to invite user');
    }
  }

  async removeUsers(userIds) {
    try {
      const token = TokenManager.getAccessToken();
      if (!token) {
        throw new Error('No token available');
      }

      const response = await fetch(`${this.apiBaseUrl}/organizations/users`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ user_ids: userIds }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `Failed to remove users (${response.status})`;
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(error?.message || error?.detail || 'Failed to remove users');
    }
  }

  async logout() {
    try {
      const refreshToken = TokenManager.getRefreshToken();
      
      // Call backend logout
      const token = TokenManager.getAccessToken();
      if (token) {
        try {
          await fetch(`${this.apiBaseUrl}/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
        } catch (e) {
          // Ignore logout errors
        }
      }

      // Clear tokens
      TokenManager.clearTokens();
    } catch (error) {
      // Clear tokens even if logout fails
      TokenManager.clearTokens();
      throw error;
    }
  }

  async refreshToken() {
    try {
      const refreshToken = TokenManager.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const formData = new URLSearchParams();
      formData.append('grant_type', 'refresh_token');
      formData.append('client_id', KEYCLOAK_CLIENT_ID);
      formData.append('refresh_token', refreshToken);

      const response = await fetch(this.keycloakTokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const tokenData = await response.json();
      TokenManager.setTokens(
        tokenData.access_token,
        tokenData.refresh_token,
        tokenData.expires_in
      );

      return tokenData;
    } catch (error) {
      TokenManager.clearTokens();
      throw error;
    }
  }

  async getUserProfile() {
    try {
      const token = TokenManager.getAccessToken();
      if (!token) {
        throw new Error('No token available');
      }

      const response = await fetch(`${this.apiBaseUrl}/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired, try to refresh
          await this.refreshToken();
          return this.getUserProfile(); // Retry with new token
        }
        throw new Error('Failed to get user profile');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Failed to get user profile');
    }
  }

  async getOrganizations() {
    try {
      const response = await fetch(`${this.apiBaseUrl}/organizations`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to get organizations');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Failed to get organizations');
    }
  }

  async getRoles() {
    try {
      const response = await fetch(`${this.apiBaseUrl}/roles`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to get roles');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Failed to get roles');
    }
  }

  async forgotPassword(email) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || `Request failed (${response.status})`;
        throw new Error(errorMessage);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(error?.message || error?.detail || 'Failed to request password reset');
    }
  }

  isAuthenticated() {
    return TokenManager.hasTokens() && !TokenManager.isTokenExpired();
  }
}

export default new AuthService();
